/* global globalThis */
// Волна 32, блок E «экономика» (гейты isPreW32Economy*, abTestGates.js).
// Чистые функции и константы блока: присоединение руин стройкой (E2-E4) и
// поздняя экономика (E5-E6). Константы лежат здесь, а не в BOT_WEIGHTS: при
// запечке или откате меняется один файл (как в logistics.js)
import {FINAL_ROUND} from "../../../configs/game";
import {KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {POLICY_FANATICISM_RUINS_WIN_POINTS_INCOME_BONUS} from "../../../configs/policy/fanaticism";
import {getNeighborHexKeys} from "../botGrid";
import {
  checkIfKeyPointToken,
  checkIfRuinsToken,
  getKeyPointHorizonEndRound,
  getKeyPointHorizonValue,
} from "./keyPoints";

// Контроль ключевой точки городом дает +1 очко сразу при присоединении
export const KEY_POINT_JOIN_WIN_POINTS = 1;

// ---- общее: зона чужих отрядов ----
// Ячейки, на которых или рядом с которыми стоит не свой отряд (включая
// нейтральный): там строить нельзя — то же, что checkIfHexBlockedByEnemyUnit
// без tokenType, но одним множеством на план
export const calcEnemyUnitZoneCells = (units, playerId, gridRenderer) => {
  const cells = new Set();
  (units || []).forEach((unit) => {
    if (unit.ownerId === playerId) {
      return;
    }
    cells.add(unit.hexKey);
    getNeighborHexKeys(unit.hexKey, gridRenderer).forEach((cellKey) => cells.add(cellKey));
  });
  return cells;
};

// ---- E2: резерв монет при присоединении руин ----
export const checkIfCellJoinsUncontrolledRuins = (cellKey, hexes, controlledKeyPoints, gridRenderer) =>
  getNeighborHexKeys(cellKey, gridRenderer).some((adjKey) =>
    checkIfRuinsToken(hexes[adjKey]) && !controlledKeyPoints.has(adjKey));

/**
 * Резерв монет для постройки, присоединяющей неконтролируемые руины.
 * R13 — 0 (найма нет). Угроза городу или армия из 0 отрядов — прежний резерв.
 * Иначе 0: руины приносят 12-24 очка, найм сдвигается максимум на раунд
 */
export const calcE32RuinsJoinCoinReserve = ({coinReserve, isFinalRound, playerUnitsCount, hasCityThreat}) => {
  if (isFinalRound) {
    return 0;
  }
  if (hasCityThreat || playerUnitsCount === 0) {
    return coinReserve;
  }
  return 0;
};

// ---- E3: цепочка «дорога -> ЦК у руин» ----
export const E32_CHAIN_RUINS_MAX_CITY_DISTANCE = 4; // руины не дальше 4 BFS-клеток от своих городских ячеек
export const E32_CHAIN_ROAD_SHARE = 0.5; // доля оценки ЦК на площадке, переносимая на дорогу-опору
export const E32_CHAIN_UNSUPPLIED_CAP = 12; // голодный отряд: ниже FARM_UNSUPPLIED_UNIT_BONUS (14)
export const E32_CHAIN_MIN_ENEMY_UNIT_DISTANCE = 3; // отряд игрока ближе к площадке — город станет подарком
export const E32_CHAIN_MIN_ROUND = 2; // дебют 1 раунда не трогаем

/**
 * Площадки под ЦК у неконтролируемых руин, недоступные сейчас, но доступные после
 * одной дороги рядом: Map<siteCellKey, ruinsHexKey[]>. Площадка:
 * - смежна руинам/великим руинам, которые не контролируются и не дальше
 *   maxCityDistance от своих городских ячеек;
 * - пуста или дорога;
 * - вне зоны кварталов (quarterZoneCells: <= 2 от любого квартала, включая свои и pending);
 * - сейчас НЕ доступна под ЦК (иначе это обычный кандидат «1 шаг»);
 * - без чужого отряда на ней и рядом;
 * - в радиусе экспансии DEFENSIVE
 */
export const findE32RuinsChainSites = ({
  hexes,
  gridRenderer,
  controlledKeyPoints,
  ownCityDistances,
  quarterZoneCells,
  centralQuarterAvailableCells,
  enemyUnitZoneCells,
  isWithinExpansionRadius = () => true,
  maxCityDistance = E32_CHAIN_RUINS_MAX_CITY_DISTANCE,
}) => {
  const sites = new Map();
  if (!quarterZoneCells || !ownCityDistances) {
    return sites;
  }
  // Обход ключей без Object.entries: зовется на каждый план стройки, а пары
  // [ключ, токен] на все поле — лишние аллокации. Порядок ключей тот же
  // (ключи «q,r» не индексы массива)
  for (const ruinsHexKey in hexes) {
    if (!checkIfRuinsToken(hexes[ruinsHexKey]) || controlledKeyPoints.has(ruinsHexKey)) {
      continue;
    }
    const cityDistance = ownCityDistances.get(ruinsHexKey);
    if (cityDistance === undefined || cityDistance > maxCityDistance) {
      continue;
    }
    getNeighborHexKeys(ruinsHexKey, gridRenderer).forEach((siteCellKey) => {
      const siteToken = hexes[siteCellKey];
      if ((siteToken && siteToken !== TOKENS.ROAD)
        || quarterZoneCells.has(siteCellKey)
        || (centralQuarterAvailableCells && centralQuarterAvailableCells.has(siteCellKey))
        || (enemyUnitZoneCells && enemyUnitZoneCells.has(siteCellKey))
        || !isWithinExpansionRadius(siteCellKey)
      ) {
        return;
      }
      const ruinsList = sites.get(siteCellKey) || [];
      ruinsList.push(ruinsHexKey);
      sites.set(siteCellKey, ruinsList);
    });
  }
  return sites;
};

// Дорога на ячейке, смежной площадке, дает ей путь строительства: Map<roadCellKey, siteCellKey[]>.
// Легальность самой дороги проверяет реальный калькулятор (кандидаты дорог)
export const buildE32RoadEnablerIndex = (sites, hexes, gridRenderer) => {
  const index = new Map();
  sites.forEach((ruinsHexKeys, siteCellKey) => {
    getNeighborHexKeys(siteCellKey, gridRenderer).forEach((roadCellKey) => {
      if (checkIfKeyPointToken(hexes[roadCellKey])) {
        return;
      }
      const siteList = index.get(roadCellKey) || [];
      siteList.push(siteCellKey);
      index.set(roadCellKey, siteList);
    });
  });
  return index;
};

/**
 * Оценка дороги-опоры, если ЦК на площадке встает в ЭТОМ ходу: нужны 2 действия
 * и монеты на дорогу + ЦК сверх резерва присоединения руин
 */
export const calcE32ChainRoadScore = ({
  siteScore,
  actionsRemaining,
  coinsAfterChain,
  joinCoinReserve,
  nearestEnemyUnitDistance = Infinity,
  roadThreatFactor = 1,
  hasUnsuppliedUnits = false,
}) => {
  if (!(siteScore > 0) || actionsRemaining < 2 || coinsAfterChain < joinCoinReserve) {
    return 0;
  }
  if (nearestEnemyUnitDistance < E32_CHAIN_MIN_ENEMY_UNIT_DISTANCE) {
    return 0;
  }
  const score = siteScore * E32_CHAIN_ROAD_SHARE * roadThreatFactor;
  return hasUnsuppliedUnits ? Math.min(score, E32_CHAIN_UNSUPPLIED_CAP) : score;
};

// ---- E4: продолжение нового города ----
export const E32_CITY_FOLLOW_UP_FACTOR = 0.35;
const FOLLOW_UP_TOKENS = new Set([KEY_POINT_TOKENS.RUINS, KEY_POINT_TOKENS.SUPER_RUINS, KEY_POINT_TOKENS.SUPER_MINE]);

/**
 * Ценность точек «на кольце» будущего ЦК: руины, великие руины, великий рудник на
 * расстоянии ровно 2 от ячейки ЦК, не контролируются, и есть слот (смежен и ЦК,
 * и точке), куда следующим ходом встанет базовый квартал: пуст или дорога, не
 * запланирован, вне зоны кварталов, без чужого отряда рядом. Горизонт — со
 * следующего раунда по последний раунд партии (ruinsIncomeRoundLimit, см.
 * getKeyPointHorizonValue). Каждая точка считается один раз
 */
export const calcE32CityFollowUpValue = ({
  cqCellKey,
  hexes,
  gridRenderer,
  controlledKeyPoints,
  plannedCells,
  quarterZoneCells,
  enemyUnitZoneCells,
  round,
  hasFanaticism = false,
  keyPointCenterBias = null,
  ruinsIncomeRoundLimit = FINAL_ROUND,
}) => {
  const horizonEndRound = getKeyPointHorizonEndRound(ruinsIncomeRoundLimit);
  if (!quarterZoneCells || !Number.isFinite(round) || round >= horizonEndRound) {
    return 0;
  }
  const cqNeighbors = getNeighborHexKeys(cqCellKey, gridRenderer);
  const cqNeighborSet = new Set(cqNeighbors);
  const countedKeyPoints = new Set();
  let value = 0;
  cqNeighbors.forEach((slotCellKey) => {
    const slotToken = hexes[slotCellKey];
    if ((slotToken && slotToken !== TOKENS.ROAD)
      || (plannedCells && plannedCells.has(slotCellKey))
      || quarterZoneCells.has(slotCellKey)
      || (enemyUnitZoneCells && enemyUnitZoneCells.has(slotCellKey))
    ) {
      return;
    }
    getNeighborHexKeys(slotCellKey, gridRenderer).forEach((kpHexKey) => {
      if (kpHexKey === cqCellKey || cqNeighborSet.has(kpHexKey) || countedKeyPoints.has(kpHexKey)) {
        return;
      }
      const token = hexes[kpHexKey];
      if (!FOLLOW_UP_TOKENS.has(token) || controlledKeyPoints.has(kpHexKey)) {
        return;
      }
      countedKeyPoints.add(kpHexKey);
      let kpHorizon = getKeyPointHorizonValue(token, round + 1, ruinsIncomeRoundLimit);
      if (hasFanaticism && checkIfRuinsToken(token)) {
        kpHorizon += POLICY_FANATICISM_RUINS_WIN_POINTS_INCOME_BONUS * (horizonEndRound - round);
      }
      value += kpHorizon * (keyPointCenterBias ? keyPointCenterBias(kpHexKey) : 1);
    });
  });
  return value * E32_CITY_FOLLOW_UP_FACTOR;
};

// ---- E5/E6: поздняя экономика ----
/**
 * Доля базы ЦК «платформа будущего расширения»: 1 до R5 включительно, затем
 * линейно 0.8/0.6/0.4/0.2 и 0 с R10 (расширяться поздно некогда)
 */
export const calcCityPlatformFactor = (round) => {
  if (!Number.isFinite(round)) {
    return 1;
  }
  const incomeRoundsLeft = Math.max(0, FINAL_ROUND - round + 1);
  return Math.min(1, Math.max(0, (incomeRoundsLeft - 4) / 5));
};

// Доля цены строения, вычитаемая из оценки: 0 до R7, 1/3 R8, 2/3 R9, 1 с R10
export const calcLateCostRamp = (round) => {
  if (!Number.isFinite(round)) {
    return 0;
  }
  return Math.min(1, Math.max(0, (round - 7) / 3));
};

export const calcLatePriceDeduction = (priceCoins, coinsToPoints, round) =>
  (priceCoins || 0) * coinsToPoints * calcLateCostRamp(round);

// Чистая стоимость ЦК в очках: +1 за каждую присоединенную точку минус цена
export const calcCentralQuarterLateNet = ({joinedKeyPointsCount, priceCoins, coinsToPoints, round}) =>
  ((joinedKeyPointsCount || 0) * KEY_POINT_JOIN_WIN_POINTS - (priceCoins || 0) * coinsToPoints)
  * calcLateCostRamp(round);

// Спецстроения, чья поздняя оценка платит цену (крепость — оборона, не очки)
export const E32_LATE_COST_TOKENS = new Set([
  SPECIAL_BUILDING_TOKENS.GUILD,
  SPECIAL_BUILDING_TOKENS.TEMPLE,
  SPECIAL_BUILDING_TOKENS.PALACE,
  SPECIAL_BUILDING_TOKENS.WAREHOUSE,
  SPECIAL_BUILDING_TOKENS.MILITARY_CAMP,
  SPECIAL_BUILDING_TOKENS.ARSENAL,
]);

// ---- счетчики харнесса (в проде globalThis.__e32Stats не задан) ----
export const recordE32Stat = (playerId, key) => {
  if (!globalThis.__e32Stats) {
    return;
  }
  const stats = globalThis.__e32Stats[playerId] || (globalThis.__e32Stats[playerId] = {});
  stats[key] = (stats[key] || 0) + 1;
};

/**
 * Дорога-опора цепочки E3 ушла в исполнение: счетчик chainRoads и площадка ЦК для
 * харнесса (globalThis.__e32ChainRoadProbe). Завершенной цепочка считается только
 * по ЦК на этой площадке — иначе в долю попадали бы обычные «дорога -> ЦК у руин»
 */
export const recordE32ChainRoad = (playerId, siteCellKey) => {
  recordE32Stat(playerId, 'chainRoads');
  if (globalThis.__e32ChainRoadProbe) {
    globalThis.__e32ChainRoadProbe(playerId, siteCellKey);
  }
};
